import { css } from "@emotion/react";
import {
	ChevronDownIcon,
	ChevronRightIcon,
	FolderClosed,
	FolderOpen,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTailwindMediaQueries } from "@/hooks/useTailwindMediaQueries";
import { useThemeState } from "@/hooks/useThemeState";
import type { Catalog } from "../../../hooks/useCatalogState";
import type {
	ProductCategory,
	ProductDepartment,
} from "../../../hooks/useInventoryState";
import { Button } from "../../ui/button";
import { CategoryControl } from "./CategoryControl";

//-------------------------------------------------------------------------------------------------------
export type DepartmentControlProps = {
	catalog: Catalog;
	department: ProductDepartment;
	departments: ProductDepartment[];
};
export const DepartmentControl: React.FC<DepartmentControlProps> = (props) => {
	const { departmentid, categoryid } = useParams();
	const themeState = useThemeState();
	const mediaQueries = useTailwindMediaQueries();
	// Check if this department is selected based on URL parameters
	const isDepartmentSelected = departmentid === props.department.Id;

	// Auto-expand department if it's selected or has a selected category
	const shouldAutoExpand = Boolean(
		isDepartmentSelected ||
			(departmentid === props.department.Id && categoryid),
	);

	const [isFilterActive, setIsFilterActive] = useState<boolean>(
		shouldAutoExpand || props.departments.length === 1,
	);

	// Auto-expand when department becomes selected
	useEffect(() => {
		if (shouldAutoExpand && !isFilterActive) {
			setIsFilterActive(true);
		}
	}, [shouldAutoExpand, isFilterActive]);

	return (
		<React.Fragment key={props.department.Id}>
			<Button
				variant="ghost"
				size="xs"
				className="w-full text-sm underline-offset-1 whitespace-normal text-left h-auto flex items-center pt-0 cursor-pointer"
				onClick={async () => {
					return Promise.resolve().then(() => {
						setIsFilterActive((prev) => !prev);
					});
				}}
			>
				<div
					css={css`
                        flex:1 1 auto;
                        text-indent: -.5em;
                        display: flex;
                        align-items: center;
                    `}
				>
					<div
						css={css`color: ${themeState.isDark() ? "inherit" : "#333333"};flex: 0 0 auto;display:flex;align-items:center;`}
					>
						{!isFilterActive && (
							<FolderClosed size={mediaQueries.desktop ? 16 : 22} />
						)}
						{isFilterActive && (
							<FolderOpen size={mediaQueries.desktop ? 16 : 22} />
						)}
					</div>
					<div
						css={css`color: ${themeState.isDark() ? "inherit" : "#333333"};flex: 1 1 0;padding:${mediaQueries.desktop ? "0" : "4px"} 14px;font-size:${mediaQueries.desktop ? ".8rem" : "1rem"};`}
					>
						{props.department.Name}
					</div>
					{(props.department.hasExpander ||
						props.department.hasExpander === undefined) && (
						<div
							css={css`
                                display: flex;
                                align-items: center;
                                flex:0 0 auto;
                            `}
						>
							{!isFilterActive && <ChevronRightIcon size={16} />}
							{isFilterActive && <ChevronDownIcon size={16} />}
						</div>
					)}
				</div>
			</Button>
			{isFilterActive && (
				<div css={css`margin:0 0 0 14px;border-left: 1px dotted #aaaaaa;`}>
					{props.department.Categories?.map(
						(productCategory: ProductCategory) => (
							<CategoryControl
								key={productCategory.Id}
								catalog={props.catalog}
								department={props.department}
								category={productCategory}
								isParentFilterActive={isFilterActive}
							/>
						),
					)}
				</div>
			)}
		</React.Fragment>
	);
};
